import React, { useState } from 'react';

import { TDashboardPageData } from 'types/state';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { DashboardTableColumn } from 'containers/DashboardTable/DashboardTableColumn';
import { DashboardTableRow } from 'containers/DashboardTable/DashboardTableRow';
import { DashboardTableWrapper } from 'containers/DashboardTable/DashboardTableWrapper';
import { DashboardTableCell } from 'containers/DashboardTable/DashboardTableCell';
import { DashboardTableLabel } from 'containers/DashboardTable/DashboardTableLabel';
import { DashboardPasswordEditor } from 'containers/DashboardPasswordEditor';
import { TextComponent } from 'design/text/textComponents';
import { ClickControlComponent } from 'design/clickControl/clickControlComponents';

type TProps = {
  content: TDashboardPageData;
  onContentEdited(): void;
};

export const DashboardTable = (props: TProps) => {
  const { content } = props;

  const intl = useIntlDictionary();

  const [editedPasswordPartnerId, setEditedPasswordPartnerId] = useState<
    number
  >();

  // Обработчик намерения отредактировать пароль.
  const onEditPartnerPassword = React.useCallback((id: number) => {
    setEditedPasswordPartnerId(id);
  }, []);

  // Обработчик успешной отправки отредактированного пароля.
  const onPartnerPasswordEditorDidSubmit = React.useCallback(() => {
    setEditedPasswordPartnerId(undefined);
  }, []);

  // Обработчик намерения отменнить редактирование пароля.
  const onClosePartnerPasswordEditor = React.useCallback(() => {
    setEditedPasswordPartnerId(undefined);
  }, []);

  const [hasOwnPasswordEditor, setHasOwnPasswordEditor] = useState(false);

  // Обработчик намерения отредактировать пароль.
  const onEditOwnPassword = React.useCallback(() => {
    setHasOwnPasswordEditor(true);
  }, []);

  // Обработчик успешной отправки отредактированного пароля.
  const onOwnPasswordEditorDidSubmit = React.useCallback(() => {
    setHasOwnPasswordEditor(false);
  }, []);

  // Обработчик намерения отменнить редактирование пароля.
  const onCloseOwnPasswordEditor = React.useCallback(() => {
    setHasOwnPasswordEditor(false);
  }, []);

  const { accountInfo, promoAccountsInfo } = content;

  return (
    <>
      <DashboardTableWrapper>
        {/* Account block */}
        <DashboardTableRow title={intl.dashboardRowUserAccountTitle}>
          <DashboardTableColumn>
            <DashboardTableCell>
              <DashboardTableLabel primary text={intl.dashboardEmailLabel} />
              <DashboardTableLabel text={accountInfo.email} />
            </DashboardTableCell>
          </DashboardTableColumn>
          <DashboardTableColumn>
            <DashboardTableCell>
              <ClickControlComponent onClick={onEditOwnPassword}>
                <TextComponent color="accent">
                  {intl.dashboardChangePasswordAction}
                </TextComponent>
              </ClickControlComponent>
            </DashboardTableCell>
            {hasOwnPasswordEditor && (
              <DashboardPasswordEditor
                onSubmitSucceeded={onOwnPasswordEditorDidSubmit}
                onClose={onCloseOwnPasswordEditor}
              />
            )}
          </DashboardTableColumn>
        </DashboardTableRow>
        {/* Promo accounts block */}
        {promoAccountsInfo.map((promoAccountInfo) => (
          <DashboardTableRow title={intl.dashboardRowPromoAccountTitle}>
            <DashboardTableColumn>
              <DashboardTableCell>
                <DashboardTableLabel primary text={intl.dashboardEmailLabel} />
                <DashboardTableLabel text={promoAccountInfo.email} />
              </DashboardTableCell>
            </DashboardTableColumn>

            <DashboardTableColumn>
              <DashboardTableCell>
                <ClickControlComponent
                  onClick={() => {
                    onEditPartnerPassword(promoAccountInfo.id);
                  }}
                >
                  <TextComponent color="accent">
                    {intl.dashboardChangePasswordAction}
                  </TextComponent>
                </ClickControlComponent>
                {editedPasswordPartnerId && (
                  <DashboardPasswordEditor
                    accountId={editedPasswordPartnerId}
                    onSubmitSucceeded={onPartnerPasswordEditorDidSubmit}
                    onClose={onClosePartnerPasswordEditor}
                  />
                )}
              </DashboardTableCell>
            </DashboardTableColumn>
          </DashboardTableRow>
        ))}
      </DashboardTableWrapper>
    </>
  );
};
